import SyncIcon from "@mui/icons-material/Sync";
import { Box, Button, FormHelperText, Grid, Tooltip, Typography } from "@mui/material";
import { SaveAnimationGif, SuccessIcon, WarningIcon } from "@platformx/shared/static-assets";
import {
  CommonPlateformXDialog,
  DashboardLoader,
  getSubDomain,
  PlatFormXDateTimeFormat,
  ShowToastError,
} from "@platformx/utilities";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import dashboardApi from "../../services/dashboard.api";
import { useStyles } from "./StaticSite.styles";

const StaticSite = () => {
  const classes = useStyles();

  const [isLoaded, setIsLoaded] = useState<Boolean[]>([false, false]);

  const [isLoading, setIsLoading] = useState(false);
  const [staticSiteDetails, setStaticSiteDetails] = useState<any>(null);
  const [openEmail, setOpenEmail] = useState(false);
  const [isLive, setIsLive] = useState(false);

  const [buttonText, setButtonText] = useState<string>("Generate");

  const [message, setMessage] = useState<string[]>(["", ""]);

  const desc = [
    `Opting for Static Site would create a static copy of your website and serve it. This would reduce infra cost. Please be wary that this is an experimental feature.`,
    `At any given point, there will be one backup available and you can rollback to that version if you are not happy with the latest generated version.`,
    `If you want to get back to the live version of your site, proceed with Switch to Live. Our team would revert once this is complete.`,
  ];
  const removeHttps = (url: string) => url.replace(/^https?:\/\//, "");
  const fetchStaticSiteDetails = async () => {
    try {
      const response: any = await dashboardApi.fetchStaticSiteDetails({
        domain: getSubDomain(),
      });
      if (response.authoring_getStaticSiteDetails) {
        const { authoring_getStaticSiteDetails: content } = response;
        setStaticSiteDetails(content);
        setIsLoading(false);

        return content;
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      setIsLoading(false);
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };
  const handleGenerateOrRollback = async (id, isRestore = false) => {
    setIsLoaded((prev) => {
      const updated = [...prev];
      updated[id] = true;
      return updated;
    });
    setMessage((prev) => {
      const updated = [...prev];
      updated[0] = "";
      updated[1] = "";
      return updated;
    });
    localStorage.setItem("isRestored", JSON.stringify(isRestore));
    try {
      const inputToSend = {
        oldDomain: getSubDomain(),

        newDomain: removeHttps(getSubDomain()),

        isRestore: isRestore,
      };

      const response: any = await dashboardApi.generateStaticSite({
        input: inputToSend,
      });
      if (response.authoring_staticSiteTrigger) {
        // setMessage(response.authoring_staticSiteTrigger.message);
        setMessage((prev) => {
          const updated = [...prev];
          updated[id] = response.authoring_staticSiteTrigger.message;
          return updated;
        });
        // isLoaded[id] = false;

        fetchStaticSiteDetails().then((res) => {
          res?.staticSiteStatus === "SUCCESS" &&
            setIsLoaded((prev) => {
              const updated = [...prev];
              updated[id] = false;
              return updated;
            });
        });
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;

      ShowToastError(t("api_error_toast"), statusCode);
      //   isLoaded[id] = false;
      setIsLoaded((prev) => {
        const updated = [...prev];
        updated[id] = false;
        return updated;
      });
    }
  };

  useEffect(() => {
    // Only set message if staticSiteDetails is loaded and status is present
    if (!staticSiteDetails?.staticSiteStatus) return;

    const defaultMessage =
      staticSiteDetails.staticSiteStatus === "SUCCESS"
        ? `Currently Site is mapped to Static Site dated ${PlatFormXDateTimeFormat(Number(staticSiteDetails.updatedAt || staticSiteDetails.createdAt))}`
        : staticSiteDetails.staticSiteStatus === "INPROGRESS" &&
            localStorage.getItem("isRestored") === "false"
          ? "The static workflow is currently running in the background. Please wait for completion."
          : "";

    setMessage((prev) => {
      // Only update if message actually changed
      if (prev[0] === defaultMessage) return prev;
      const updated = [...prev];
      updated[0] = defaultMessage;
      return updated;
    });
    if (
      staticSiteDetails?.staticSiteStatus === "SUCCESS" ||
      (staticSiteDetails?.isStaticSiteBackup === true &&
        staticSiteDetails?.isBackupRestored === false) ||
      (staticSiteDetails?.isStaticSiteBackup === false &&
        staticSiteDetails?.isBackupRestored === true)
    ) {
      setButtonText("Regenerate");
    } else {
      setButtonText("Generate");
    }

    if (
      staticSiteDetails?.staticSiteStatus === "SUCCESS" &&
      staticSiteDetails?.isBackupRestored === true
    ) {
      setIsLoaded((prev) => {
        const updated = [...prev];
        updated[1] = false; // Reset the first button state to not loaded
        return updated;
      });
    }
    if (
      staticSiteDetails?.staticSiteStatus === "SUCCESS" &&
      staticSiteDetails?.isBackupRestored === false
    ) {
      setIsLoaded((prev) => {
        const updated = [...prev];
        updated[0] = false; // Reset the first button state to not loaded
        return updated;
      });
    }
  }, [staticSiteDetails]);

  useEffect(() => {
    fetchStaticSiteDetails();
  }, []);

  const handleRefresh = () => {
    setIsLoading(true);

    fetchStaticSiteDetails();
  };
  const handleSendEmail = () => {
    // const email = "to@example.com";
    // const subject = encodeURIComponent("Make Site Live");
    // const body = encodeURIComponent("This is a test message.");
    // window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    setOpenEmail(false);

    setIsLive(true);
  };
  return (
    <Box>
      <Box className={`${classes.headerItem} header`}>
        <Typography variant='h5semibold'>Generate Static Site </Typography>
        <Box
          onClick={handleRefresh}
          data-testid='content-listing-refresh'
          sx={{
            backgroundColor: "white",
            borderRadius: "4px",
            border: "1px solid #14142B",
            height: { xs: "42px", md: "30px" },
            width: "30px",
            alignItems: "center",
            cursor: "pointer",
            justifyContent: "center",
            display: "flex",
          }}>
          <SyncIcon
            sx={{
              color: "#2d2d39",
              verticalAlign: "middle",
              fontSize: { md: "21px" },
              cursor: "pointer",
            }}
            // className={animationState ? classes.rotateIcon : ""}
          />
        </Box>
      </Box>
      {isLoading ? (
        <DashboardLoader />
      ) : (
        <Grid container direction='column'>
          {[0, 1, 2].map((row) => (
            <Grid
              item
              key={row}
              className={classes.Tasklistbox}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                borderBottom: row < 2 ? "1px solid #ccc" : "none",
                minHeight: 64,
                boxSizing: "border-box",
                marginBottom: row === 2 ? "-10px" : 0,
                flexDirection: "column",
              }}>
              {/* First column: description */}
              <Box
                sx={{
                  flexBasis: "100%",
                  maxWidth: "100%",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  boxSizing: "border-box",
                  padding: "0 15px",
                  flexDirection: "column",
                }}>
                <Typography component='div' variant='h6regular' className={classes.description}>
                  {desc[row]}
                </Typography>
                {row === 1 && (
                  <Typography component='div' sx={{ textAlign: "left", width: "100%" }}>
                    <Box>
                      <FormHelperText error={true}>
                        Note: You can roll back to one version.
                      </FormHelperText>
                    </Box>
                  </Typography>
                )}
              </Box>
              {/* Second column: button */}

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "100%",
                  width: "100%",
                  boxSizing: "border-box",
                  marginTop: "15px",
                  padding: "0 15px",
                }}>
                {/* Left: Typography/message */}
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  {row === 0 && message[row] && (
                    <>
                      <img
                        src={
                          staticSiteDetails?.staticSiteStatus === "SUCCESS" && !isLoaded[0]
                            ? SuccessIcon
                            : WarningIcon
                        }
                        alt='success'
                        style={{ height: "20px", width: "17px", marginRight: "5px" }}
                      />
                      <Typography variant='p4regular' component='div'>
                        {`${message[row]}`}
                      </Typography>
                    </>
                  )}
                  {row === 1 && message[row] && (
                    <>
                      <img
                        src={
                          staticSiteDetails?.staticSiteStatus === "SUCCESS" && !isLoaded[1]
                            ? SuccessIcon
                            : WarningIcon
                        }
                        alt='success'
                        style={{ height: "20px", width: "17px", marginRight: "5px" }}
                      />
                      <Typography variant='p4regular' component='div'>
                        {staticSiteDetails?.staticSiteStatus === "INPROGRESS" || isLoaded[row]
                          ? "Backup restore is currently running in the background. Please wait for completion "
                          : `${message[row].replace(/\.$/, "")} dated ${PlatFormXDateTimeFormat(Number(staticSiteDetails.updatedAt || staticSiteDetails.createdAt))}`}
                      </Typography>
                    </>
                  )}
                </Box>
                {/* Right: Button */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    minWidth: "180px",
                    marginLeft: 2,
                  }}>
                  {row === 0 && (
                    <Tooltip
                      title={
                        staticSiteDetails?.staticSiteStatus === "INPROGRESS"
                          ? "Please refresh after sometime to see the status"
                          : ""
                      }
                      placement='top'>
                      <span>
                        <Button
                          variant='primaryButton'
                          className='sm'
                          disabled={staticSiteDetails?.staticSiteStatus === "INPROGRESS"}
                          onClick={() => handleGenerateOrRollback(row)}>
                          {(isLoaded[row] ||
                            (staticSiteDetails?.staticSiteStatus === "INPROGRESS" &&
                              (localStorage.getItem("isRestored") === "false" ||
                                localStorage.getItem("isRestored") === null))) && (
                            <img src={SaveAnimationGif} alt='' />
                          )}
                          {isLoaded[row] ||
                          (staticSiteDetails?.staticSiteStatus === "INPROGRESS" &&
                            (localStorage.getItem("isRestored") === "false" ||
                              localStorage.getItem("isRestored") === null))
                            ? buttonText === "Generate"
                              ? "Generating"
                              : "Regenerating"
                            : buttonText}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {row === 1 && (
                    <span>
                      <Button
                        variant='primaryButton'
                        className='sm'
                        onClick={() => handleGenerateOrRollback(row, true)}
                        disabled={
                          !(
                            staticSiteDetails?.isStaticSiteBackup === true &&
                            staticSiteDetails?.isBackupRestored === false
                          ) || staticSiteDetails?.staticSiteStatus === "INPROGRESS"
                        }>
                        {(isLoaded[row] ||
                          (staticSiteDetails?.staticSiteStatus === "INPROGRESS" &&
                            localStorage.getItem("isRestored") === "true")) && (
                          <img src={SaveAnimationGif} alt='' />
                        )}
                        {isLoaded[row] ||
                        (staticSiteDetails?.staticSiteStatus === "INPROGRESS" &&
                          localStorage.getItem("isRestored") === "true")
                          ? "Rollbacking"
                          : "Rollback"}
                      </Button>
                    </span>
                  )}
                  {row === 2 && (
                    <Button
                      variant='primaryButton'
                      className='sm'
                      disabled={true}
                      onClick={() => {
                        setOpenEmail(true);
                      }}>
                      Switch To Live
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      {openEmail ? (
        <CommonPlateformXDialog
          isDialogOpen={openEmail}
          title={`We are happy to help!`}
          subTitle={`Do you really want to make your site live?`}
          closeButtonText={"Cancel"}
          confirmButtonText={"Yes"}
          closeButtonHandle={() => setOpenEmail(false)}
          confirmButtonHandle={handleSendEmail}
          crossButtonHandle={() => setOpenEmail(false)}
          modalType='publish'
          disableConfirmButton={true}
        />
      ) : null}
      {isLive ? (
        <CommonPlateformXDialog
          isDialogOpen={isLive}
          title={``}
          subTitle={`We have triggered an email to our Admin, Please expect a response from them shorlty.`}
          closeButtonText={"Close"}
          // confirmButtonText={"Yes"}
          closeButtonHandle={() => setIsLive(false)}
          confirmButtonHandle={handleSendEmail}
          crossButtonHandle={() => setIsLive(false)}
          modalType='publish'
        />
      ) : null}
    </Box>
  );
};
export default React.memo(StaticSite);
